/**
 * Catalogue search and filtering. Pure functions over the manifest index, so
 * they are testable without rendering anything.
 */
import type { CraftableWeaponSummary } from "@/lib/crafting/types";
import { ammoLabel } from "@/lib/bungie/assets";

export interface CatalogueFilters {
  search: string;
  weaponTypes: string[];
  elements: string[];
  ammoTypes: number[];
}

export const EMPTY_FILTERS: CatalogueFilters = {
  search: "",
  weaponTypes: [],
  elements: [],
  ammoTypes: [],
};

export interface ElementFacet {
  name: string;
  /** Manifest icon path, so the filter shows the real element symbol. */
  icon?: string;
}

export interface CatalogueFacets {
  weaponTypes: string[];
  elements: ElementFacet[];
  ammoTypes: { value: number; label: string }[];
}

/**
 * The filter options actually present in the data. Derived rather than
 * hardcoded so a new weapon type or damage type appears without a code change.
 */
export function facetsFor(weapons: readonly CraftableWeaponSummary[]): CatalogueFacets {
  const weaponTypes = new Set<string>();
  // Keyed by name so the first weapon carrying an icon supplies it for the facet.
  const elements = new Map<string, ElementFacet>();
  const ammoTypes = new Set<number>();

  for (const w of weapons) {
    if (w.itemTypeDisplayName) weaponTypes.add(w.itemTypeDisplayName);
    if (w.damageTypeName) {
      const existing = elements.get(w.damageTypeName);
      if (!existing) elements.set(w.damageTypeName, { name: w.damageTypeName, icon: w.damageTypeIcon });
      else if (!existing.icon && w.damageTypeIcon) existing.icon = w.damageTypeIcon;
    }
    if (typeof w.ammoType === "number" && w.ammoType > 0) ammoTypes.add(w.ammoType);
  }

  return {
    weaponTypes: [...weaponTypes].sort((a, b) => a.localeCompare(b)),
    elements: [...elements.values()].sort((a, b) => a.name.localeCompare(b.name)),
    ammoTypes: [...ammoTypes]
      .sort((a, b) => a - b)
      .map((value) => ({ value, label: ammoLabel(value) ?? `Type ${value}` })),
  };
}

/** Case- and punctuation-insensitive, so "ikelos smg" matches "IKELOS_SMG_v1.0.3". */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function matchesSearch(weapon: CraftableWeaponSummary, search: string): boolean {
  const needle = normalize(search);
  if (!needle) return true;
  const haystack = normalize(
    [weapon.name, weapon.itemTypeDisplayName, weapon.damageTypeName].filter(Boolean).join(" ")
  );
  // Every whitespace-separated term must appear, so extra words narrow rather
  // than widen the result.
  return needle.split(" ").every((term) => haystack.includes(term));
}

/** Filters combine with AND across categories, OR within a category. */
export function filterWeapons(
  weapons: readonly CraftableWeaponSummary[],
  filters: CatalogueFilters
): CraftableWeaponSummary[] {
  return weapons.filter((w) => {
    if (!matchesSearch(w, filters.search)) return false;
    if (
      filters.weaponTypes.length > 0 &&
      !(w.itemTypeDisplayName && filters.weaponTypes.includes(w.itemTypeDisplayName))
    ) {
      return false;
    }
    if (
      filters.elements.length > 0 &&
      !(w.damageTypeName && filters.elements.includes(w.damageTypeName))
    ) {
      return false;
    }
    if (
      filters.ammoTypes.length > 0 &&
      !(typeof w.ammoType === "number" && filters.ammoTypes.includes(w.ammoType))
    ) {
      return false;
    }
    return true;
  });
}

export function activeFilterCount(filters: CatalogueFilters): number {
  return (
    filters.weaponTypes.length +
    filters.elements.length +
    filters.ammoTypes.length +
    (filters.search.trim() ? 1 : 0)
  );
}

/** Add or remove one value from a multi-select filter. */
export function toggleValue<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}
